import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Zap, Info, CheckCircle, X } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { ScoreHero } from '../../components/performance/ScoreHero'
import ScoresChart from '../../components/performance/ScoresChart'
import { CwvCard } from '../../components/performance/CwvCard'

// ─── Types ──────────────────────────────────────────────────────────────────

interface AuditHistory {
  id: number
  createdAt: string
  avgPerfMobile: number | null
  avgPerfDesktop: number | null
  avgSeo: number | null
  avgBp: number | null
  avgLcp: number | null
  avgFcp: number | null
  avgCls: number | null
  avgTtfb: number | null
  avgInp: number | null
}

interface Audit {
  id: number
  status: string
  createdAt: string
  avgPerfMobile: number | null
  avgPerfDesktop: number | null
  avgLcp: number | null
  avgFcp: number | null
  avgCls: number | null
  avgTtfb: number | null
  avgInp: number | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CWV_METRICS = ['LCP', 'FCP', 'CLS', 'TTFB', 'INP'] as const
type CwvMetricKey = typeof CWV_METRICS[number]

const METRIC_TO_FIELD: Record<CwvMetricKey, keyof AuditHistory> = {
  LCP:  'avgLcp',
  FCP:  'avgFcp',
  CLS:  'avgCls',
  TTFB: 'avgTtfb',
  INP:  'avgInp',
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function CoreWebVitals() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const clientId = projectId ?? ''

  const [launching, setLaunching] = useState(false)
  const [justLaunched, setJustLaunched] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)
  const [completedNotif, setCompletedNotif] = useState(false)
  const prevRunningRef = useRef(false)

  // Fetch history (for chart + CwvCard sparklines)
  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useApi<AuditHistory[]>(
    clientId ? `/api/perf-audits/history?clientId=${clientId}` : ''
  )

  // Fetch audit list (for completed count + last audit values)
  const {
    data: auditsData,
    loading: auditsLoading,
    refetch: refetchAudits,
  } = useApi<Audit[]>(
    clientId ? `/api/perf-audits?clientId=${clientId}` : ''
  )

  const history   = asArray(historyData)
  const allAudits = asArray(auditsData)

  const completedAudits = allAudits.filter(a => a.status === 'completed')
  const completedCount  = completedAudits.length
  const hasRunning = allAudits.some(a => a.status === 'pending' || a.status === 'running')

  // Clear optimistic state once list catches up
  useEffect(() => {
    if (justLaunched && hasRunning) setJustLaunched(false)
  }, [justLaunched, hasRunning])

  // Poll while audit is running
  useEffect(() => {
    if (!hasRunning && !justLaunched) return
    const interval = setInterval(() => {
      refetchAudits()
      refetchHistory()
    }, 5000)
    return () => clearInterval(interval)
  }, [hasRunning, justLaunched, refetchAudits, refetchHistory])

  // Detect completion → show notification
  useEffect(() => {
    if (prevRunningRef.current && !hasRunning && !justLaunched) {
      setCompletedNotif(true)
    }
    prevRunningRef.current = hasRunning
  }, [hasRunning, justLaunched])

  // Last and second-to-last completed audits
  const lastAudit     = completedAudits[0] ?? null
  const prevAudit     = completedAudits[1] ?? null

  const launchDisabled = launching || hasRunning || justLaunched

  // ─── Actions ───────────────────────────────────────────────────────────────

  async function handleLaunchAudit() {
    if (!clientId || launchDisabled) return
    setLaunching(true)
    setLaunchError(null)
    setCompletedNotif(false)
    try {
      const res = await fetch('/api/perf-audits', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId }),
      })
      const data = await res.json()
      if (!res.ok) {
        setLaunchError(data.error || 'Erreur lors du lancement')
      } else {
        setJustLaunched(true)
      }
      refetchAudits()
      refetchHistory()
    } finally {
      setLaunching(false)
    }
  }

  // ─── Loading ───────────────────────────────────────────────────────────────

  const loading = historyLoading || auditsLoading

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
        <div className="flex gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-28 w-[200px] bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (historyError) {
    return (
      <div className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
        Erreur lors du chargement : {historyError}
      </div>
    )
  }

  // ─── Mode empty ────────────────────────────────────────────────────────────

  if (completedCount === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">Core Web Vitals</h1>

        {/* Audit en cours */}
        {(hasRunning || justLaunched) && (
          <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-5 py-4">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <Zap size={16} className="text-indigo-500 animate-pulse" />
            </div>
            <div>
              <p className="text-sm font-medium text-indigo-800">Audit en cours...</p>
              <p className="text-xs text-indigo-600 mt-0.5">
                Les r&eacute;sultats appara&icirc;tront automatiquement une fois l'audit termin&eacute;.
              </p>
            </div>
          </div>
        )}

        {launchError && (
          <p className="text-red-500 text-sm">{launchError}</p>
        )}

        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50">
            <Zap size={32} className="text-indigo-400" />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-700">
              Aucun audit de performance
            </p>
            <p className="mt-1 text-sm text-gray-400 max-w-xs">
              Lancez un premier audit pour analyser les performances de votre site.
            </p>
          </div>
          <button
            onClick={handleLaunchAudit}
            disabled={launchDisabled}
            className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {(launching || justLaunched) ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Zap size={14} />
            )}
            {launching ? 'Lancement...' : justLaunched ? 'Audit lanc\u00e9...' : 'Lancer un audit'}
          </button>
        </div>
      </div>
    )
  }

  // ─── Score + CWV values ────────────────────────────────────────────────────

  const currentScore  = lastAudit?.avgPerfMobile ?? 0
  const previousScore = prevAudit?.avgPerfMobile ?? undefined

  // CwvCard values from last audit
  function getCwvValue(metric: CwvMetricKey): number | null {
    if (!lastAudit) return null
    const field = METRIC_TO_FIELD[metric]
    return (lastAudit as unknown as Record<string, number | null>)[field as string] ?? null
  }

  // CwvCard history arrays (one value per audit, oldest→newest)
  function getCwvHistory(metric: CwvMetricKey): number[] | undefined {
    if (completedCount < 2) return undefined
    const field = METRIC_TO_FIELD[metric]
    return [...history]
      .reverse()
      .map(h => (h[field] as number | null) ?? 0)
  }

  // CwvCard history dates (one ISO date per audit, oldest→newest)
  function getHistoryDates(): string[] | undefined {
    if (completedCount < 2) return undefined
    return [...history].reverse().map(h => h.createdAt)
  }

  // ─── Mode snapshot (1 audit) ───────────────────────────────────────────────

  // Shared status banners (reused across snapshot & evolution modes)
  const statusBanners = (
    <>
      {/* Audit en cours */}
      {(hasRunning || justLaunched) && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-5 py-4">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
            <Zap size={16} className="text-indigo-500 animate-pulse" />
          </div>
          <div>
            <p className="text-sm font-medium text-indigo-800">Audit en cours...</p>
            <p className="text-xs text-indigo-600 mt-0.5">
              Les r&eacute;sultats seront mis &agrave; jour automatiquement.
            </p>
          </div>
        </div>
      )}

      {/* Notification: audit terminé */}
      {completedNotif && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-800">Audit termin&eacute; !</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Les scores ont &eacute;t&eacute; mis &agrave; jour. Consultez aussi{' '}
              <button
                onClick={() => navigate(`/p/${projectId}/performance/pagespeed`)}
                className="underline hover:no-underline font-medium"
              >
                PageSpeed
              </button>{' '}
              pour le d&eacute;tail des opportunit&eacute;s.
            </p>
          </div>
          <button
            onClick={() => setCompletedNotif(false)}
            className="p-1 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            <X className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {launchError && (
        <p className="text-red-500 text-sm">{launchError}</p>
      )}
    </>
  )

  if (completedCount === 1) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">Core Web Vitals</h1>

        {statusBanners}

        {/* Score hero */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center gap-2">
          <ScoreHero
            score={Math.round(currentScore)}
            label="Performance Mobile"
          />
        </div>

        {/* CWV cards */}
        <div className="flex flex-wrap gap-3">
          {CWV_METRICS.map(metric => (
            <CwvCard
              key={metric}
              metric={metric}
              value={getCwvValue(metric)}
              projectId={clientId}
            />
          ))}
        </div>

        {/* Info bandeau */}
        <div className="flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3 text-sm text-indigo-700">
          <Info size={15} className="mt-0.5 shrink-0" />
          <span>
            Lancez un second audit pour voir l'évolution des scores dans le temps.
          </span>
        </div>

        {/* Scheduling placeholder */}
        <SchedulingSection onLaunch={handleLaunchAudit} launching={launchDisabled} />
      </div>
    )
  }

  // ─── Mode evolution (2+ audits) ────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-gray-900">Core Web Vitals</h1>

      {statusBanners}

      {/* Score hero */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center gap-2">
        <ScoreHero
          score={Math.round(currentScore)}
          previousScore={previousScore !== undefined ? Math.round(previousScore) : undefined}
          label="Performance Mobile"
        />
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <ScoresChart history={history} />
      </div>

      {/* CWV cards */}
      <div className="flex flex-wrap gap-3">
        {CWV_METRICS.map(metric => (
          <CwvCard
            key={metric}
            metric={metric}
            value={getCwvValue(metric)}
            history={getCwvHistory(metric)}
            dates={getHistoryDates()}
            projectId={clientId}
          />
        ))}
      </div>

      {/* Scheduling placeholder */}
      <SchedulingSection onLaunch={handleLaunchAudit} launching={launchDisabled} />
    </div>
  )
}

// ─── Scheduling section ───────────────────────────────────────────────────────

function SchedulingSection({
  onLaunch,
  launching,
}: {
  onLaunch: () => void
  launching: boolean
}) {
  return (
    <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-4">
      <div>
        <p className="text-sm font-medium text-gray-700">Programmez un audit récurrent</p>
        <p className="text-xs text-gray-400 mt-0.5">
          Automatisez vos audits pour suivre l'évolution dans le temps.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onLaunch}
          disabled={launching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-60 transition-colors"
        >
          <Zap size={13} />
          {launching ? 'Lancement...' : 'Lancer maintenant'}
        </button>
        <div className="px-3 py-1.5 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg cursor-not-allowed select-none">
          Configurer (bientôt)
        </div>
      </div>
    </div>
  )
}
